import React, { useMemo, useState } from "react";
import {
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import { SalonService } from "../../../types/context";
import {
  BogoPercentTemplateParams,
  CreateServicePackageRequest,
  PackageKind,
  PackageTemplateType,
  ServicePackage,
  SimpleDiscountTemplateParams,
  TemplateParams,
} from "../../../types/packages";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import BogoBuilder, {
  BogoBuilderState,
  bogoStateFromParams,
  bogoStateToParams,
  defaultBogoState,
} from "./BogoBuilder";
import CombinedDiscountBuilder, {
  CombinedDiscountState,
  combinedDiscountStateFromParams,
  combinedDiscountStateToParams,
  defaultCombinedDiscountState,
} from "./CombinedDiscountBuilder";
import {
  computeItems,
  computePrice,
  separatePrice,
  suggestName,
} from "./templateMath";

interface PackageEditorModalProps {
  opened: boolean;
  onClose: () => void;
  services: SalonService[];
  kind: PackageKind;
  offerType: PackageTemplateType;
  /** Present when editing an existing definition (saves as an update). */
  initialPackage?: ServicePackage;
  /** Present when duplicating: prefills the builder, clears dates, "(Copy)". */
  duplicateSource?: ServicePackage;
  onSubmit: (data: CreateServicePackageRequest) => void;
  isSaving?: boolean;
}

type EditorStep = "offer" | "details";

const normalizeIsoDate = (s: string | null | undefined): string | null => {
  if (!s) return null;
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

/**
 * Builder prefill for edit/duplicate. template_params round-trip the
 * builder; if a row somehow lacks them, fall back to a flat simple
 * discount reconstructed from the stored items + price.
 */
function initialBogoState(source?: ServicePackage): BogoBuilderState {
  const params = source?.template_params as
    | BogoPercentTemplateParams
    | undefined
    | null;
  if (params && "buy_service_id" in params) return bogoStateFromParams(params);
  return defaultBogoState();
}

function initialDiscountState(
  source: ServicePackage | undefined,
  priceByServiceId: Record<string, number>,
): CombinedDiscountState {
  const params = source?.template_params as
    | SimpleDiscountTemplateParams
    | undefined
    | null;
  if (params && "items" in params) {
    return combinedDiscountStateFromParams(params, priceByServiceId);
  }
  if (source) {
    const fallback: SimpleDiscountTemplateParams = {
      items: source.items.map((item) => ({
        salon_service_id: item.salon_service_id,
        quantity: item.quantity,
      })),
      discount_type: "flat",
      discount_value: 0,
    };
    fallback.discount_value = Math.max(
      0,
      round2(separatePrice(fallback, priceByServiceId) - source.price),
    );
    return combinedDiscountStateFromParams(fallback, priceByServiceId);
  }
  return defaultCombinedDiscountState();
}

/**
 * Two-step editor for a package or special: step 1 builds the offer
 * (services, quantities, discount) with a live price comparison; step 2
 * captures the details (name, description, validity, availability, tax).
 */
export default function PackageEditorModal({
  opened,
  onClose,
  services,
  kind,
  offerType,
  initialPackage,
  duplicateSource,
  onSubmit,
  isSaving = false,
}: PackageEditorModalProps) {
  const priceByServiceId = useMemo(
    () =>
      Object.fromEntries(
        services.map((service) => [service.id, service.default_price ?? 0]),
      ),
    [services],
  );
  const nameByServiceId = useMemo(
    () =>
      Object.fromEntries(services.map((service) => [service.id, service.name])),
    [services],
  );
  const basisByServiceId = useMemo(
    () =>
      Object.fromEntries(
        services.map((service) => [service.id, service.price_basis]),
      ),
    [services],
  );

  const source = initialPackage ?? duplicateSource;

  const [step, setStep] = useState<EditorStep>("offer");
  const [bogoState, setBogoState] = useState<BogoBuilderState>(() =>
    initialBogoState(source),
  );
  const [discountState, setDiscountState] = useState<CombinedDiscountState>(
    () => initialDiscountState(source, priceByServiceId),
  );

  // ── Details state ──────────────────────────────────────────────
  const [name, setName] = useState(() =>
    initialPackage
      ? initialPackage.name
      : duplicateSource
        ? `${duplicateSource.name} (Copy)`
        : "",
  );
  // Prefilled names count as dirty so they stay authoritative until edited.
  const [nameDirty, setNameDirty] = useState(!!source);
  const [description, setDescription] = useState(source?.description ?? "");
  // "Expires after N days" is the default-on switch. New offers default to
  // 365 days; editing/duplicating reflects the stored value (off = never).
  const [expires, setExpires] = useState(
    source ? !!source.validity_days : true,
  );
  const [validityDays, setValidityDays] = useState<number | string>(
    source?.validity_days ?? 365,
  );
  const [isPurchasableOnline, setIsPurchasableOnline] = useState(
    source ? source.is_purchasable_online : true,
  );
  const [omitTax, setOmitTax] = useState(source?.omit_tax_collection ?? false);
  // Duplicated specials start with CLEARED dates — a copy is a new promo.
  const [startDate, setStartDate] = useState<string | null>(
    initialPackage?.effective_start_date ?? null,
  );
  const [endDate, setEndDate] = useState<string | null>(
    initialPackage?.effective_end_date ?? null,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Derived offer values ───────────────────────────────────────
  const templateParams: TemplateParams | null =
    offerType === "bogo_percent"
      ? bogoStateToParams(bogoState)
      : combinedDiscountStateToParams(discountState, priceByServiceId);
  const separate = separatePrice(templateParams, priceByServiceId);
  const finalPrice = computePrice(offerType, templateParams, priceByServiceId);
  const savings = round2(separate - finalPrice);
  const savingsPct = separate > 0 ? Math.round((savings / separate) * 100) : 0;

  const suggestedName = suggestName(
    offerType,
    templateParams,
    nameByServiceId,
    basisByServiceId,
  );
  const displayName = nameDirty ? name : suggestedName;

  const noun = kind === "special" ? "Special" : "Package";

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    if (!templateParams) {
      nextErrors.template = "Complete the offer before saving.";
    }
    if (!displayName.trim()) {
      nextErrors.name = "Name is required";
    }
    if (kind === "special") {
      if (!startDate) nextErrors.startDate = "Start date is required";
      if (!endDate) nextErrors.endDate = "End date is required";
      if (startDate && endDate && endDate < startDate) {
        nextErrors.dates = "End date must be on or after the start date.";
      }
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !templateParams) return;

    const validityDaysNum = Number(validityDays);
    onSubmit({
      name: displayName.trim(),
      description: description.trim() || null,
      price: finalPrice,
      validity_days:
        !expires ||
        validityDays === "" ||
        !Number.isFinite(validityDaysNum) ||
        validityDaysNum <= 0
          ? null
          : validityDaysNum,
      is_purchasable_online: isPurchasableOnline,
      omit_tax_collection: omitTax,
      items: computeItems(offerType, templateParams),
      kind,
      package_type: offerType,
      template_params: templateParams,
      effective_start_date: kind === "special" ? startDate : null,
      effective_end_date: kind === "special" ? endDate : null,
    });
  };

  const renderOfferStep = () => (
    <Stack gap="md">
      {offerType === "bogo_percent" ? (
        <BogoBuilder
          services={services}
          value={bogoState}
          onChange={setBogoState}
        />
      ) : (
        <CombinedDiscountBuilder
          services={services}
          value={discountState}
          onChange={setDiscountState}
        />
      )}

      {/* BOGO shows the separate/discounted/savings comparison here; the
          Simple-or-Bundled-Discount builder renders its own price equation. */}
      {offerType === "bogo_percent" && (
        <Paper p="md" withBorder radius="md" bg="gray.0">
          <Stack gap={4}>
            <Text size="sm" c="dimmed">
              If bought separately: {formatCurrency(separate)}
            </Text>
            <Text size="sm" fw={600}>
              Discounted price: {formatCurrency(finalPrice)}
            </Text>
            {savings > 0 && (
              <Text size="sm" c="var(--brand-teal)">
                You save {formatCurrency(savings)} ({savingsPct}%)
              </Text>
            )}
          </Stack>
        </Paper>
      )}

      {errors.template && (
        <Text size="xs" c="red">
          {errors.template}
        </Text>
      )}

      <Group justify="flex-end" gap="sm">
        <Button variant="light" color="gray" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="var(--brand-teal)"
          disabled={!templateParams || finalPrice <= 0}
          onClick={() => setStep("details")}
        >
          Next
        </Button>
      </Group>
    </Stack>
  );

  const renderDetailsStep = () => (
    <Stack gap="md">
      <TextInput
        label="Name"
        required
        value={displayName}
        onChange={(e) => {
          setNameDirty(true);
          setName(e.target.value);
        }}
        error={errors.name}
        placeholder="e.g., Buy 2 Get 1 Free — Signature Facial"
      />

      {kind === "special" && (
        <Stack gap="xs">
          <Group grow>
            <DateInput
              label="Start Date"
              required
              value={startDate}
              onChange={(value) => setStartDate(normalizeIsoDate(value))}
              error={errors.startDate}
            />
            <DateInput
              label="End Date"
              required
              value={endDate}
              onChange={(value) => setEndDate(normalizeIsoDate(value))}
              error={errors.endDate}
            />
          </Group>
          {errors.dates && (
            <Text size="xs" c="red">
              {errors.dates}
            </Text>
          )}
        </Stack>
      )}

      <Textarea
        label="Description (Optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Describe what this offer includes..."
        rows={3}
      />

      <Stack gap="xs">
        <Group gap={6} align="center">
          <Switch
            label="Expires after a set number of days"
            checked={expires}
            onChange={(event) => setExpires(event.currentTarget.checked)}
            color="var(--brand-teal)"
          />
          <Tooltip
            label="The package/special is valid for this many days after purchasing"
            position="top"
            multiline
            w={260}
            withArrow
          >
            <IconInfoCircle
              size={14}
              style={{
                color: "var(--mantine-color-dimmed)",
                cursor: "help",
              }}
            />
          </Tooltip>
        </Group>
        {expires && (
          <NumberInput
            aria-label="Expires after days"
            label="Expires after (days)"
            value={validityDays}
            onChange={setValidityDays}
            min={1}
            allowDecimal={false}
            allowNegative={false}
            hideControls
            placeholder="e.g., 365"
            w={200}
          />
        )}
      </Stack>

      <Switch
        label="Available for purchase on the Client Booking Platform"
        checked={isPurchasableOnline}
        onChange={(event) =>
          setIsPurchasableOnline(event.currentTarget.checked)
        }
        color="var(--brand-teal)"
      />

      <Group gap={6} align="center">
        <Switch
          label="Include tax in the package price"
          checked={omitTax}
          onChange={(event) => setOmitTax(event.currentTarget.checked)}
          color="var(--brand-teal)"
        />
        <Tooltip
          label="If enabled, the business will absorb any relevant taxes. Otherwise, any relevant taxes will be applied at the time of checkout."
          position="top"
          multiline
          w={300}
          withArrow
        >
          <IconInfoCircle
            size={14}
            style={{
              color: "var(--mantine-color-dimmed)",
              cursor: "help",
            }}
          />
        </Tooltip>
      </Group>

      <Group justify="space-between" gap="sm">
        <Button variant="subtle" color="gray" onClick={() => setStep("offer")}>
          Back
        </Button>
        <Group gap="sm">
          <Button variant="light" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            loading={isSaving}
            onClick={handleSubmit}
          >
            {initialPackage ? `Update ${noun}` : `Create ${noun}`}
          </Button>
        </Group>
      </Group>
    </Stack>
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      centered
      title={
        <Text fw={700} size="lg">
          {initialPackage ? `Edit ${noun}` : `New ${noun}`}
        </Text>
      }
    >
      {step === "offer" ? renderOfferStep() : renderDetailsStep()}
    </Modal>
  );
}
